import { Box, Typography } from '@mui/material';
import { Tooltip, TooltipSize } from 'framework/ui/mui-builder';
import type { SyberryTodayProjectAllocationSummary } from 'domain/people/syberryToday/types';
import { clampPercent, formatHourRange, formatHours, formatScaleHours, getOverflowWidthPercent } from './utils';

type SyberryTodayAllocationScaleProps = {
  allocationSummary: SyberryTodayProjectAllocationSummary;
};

const SyberryTodayAllocationScale = ({ allocationSummary }: SyberryTodayAllocationScaleProps) => {
  const hasExceededMaxHours = allocationSummary.exceededMaxHours > 0;
  const progressPercent = hasExceededMaxHours ? 100 : clampPercent(allocationSummary.progressPercent);
  const minAllocationMarkerPercent = clampPercent(allocationSummary.minAllocationMarkerPercent);
  const overflowWidthPercent = getOverflowWidthPercent(allocationSummary.progressPercent);
  const shouldShowMinAllocationMarker = allocationSummary.plannedMinHours > 0 && minAllocationMarkerPercent > 0;
  const plannedHoursLabel = formatHourRange(allocationSummary.plannedMinHours, allocationSummary.plannedMaxHours);
  const remainingHoursLabel = formatHourRange(allocationSummary.remainingMinHours, allocationSummary.remainingMaxHours);
  const meterValue = clampPercent(allocationSummary.progressPercent);
  const meterValueText = hasExceededMaxHours
    ? `${formatHours(allocationSummary.spentHours)} spent of ${formatHours(
        allocationSummary.plannedMaxHours,
      )} planned, over allocation ${formatHours(allocationSummary.exceededMaxHours)}`
    : `${formatHours(allocationSummary.spentHours)} spent of ${formatHours(allocationSummary.plannedMaxHours)} planned`;
  const tooltipTitle = (
    <Box>
      <Typography variant="body1">Planned: {plannedHoursLabel}</Typography>
      <Typography variant="body1">Spent time: {formatHours(allocationSummary.spentHours)}</Typography>
      <Typography variant="body1">
        {hasExceededMaxHours
          ? `Over allocation: ${formatHours(allocationSummary.exceededMaxHours)}`
          : `Remaining: ${remainingHoursLabel}`}
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} size={TooltipSize.BIG} placement="top-start">
      <Box
        role="meter"
        aria-label="Weekly allocation progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={meterValue}
        aria-valuetext={meterValueText}
        sx={{ display: 'inline-flex', alignItems: 'flex-start', width: '100%' }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            flex: '0 0 100%',
            width: '100%',
            height: '31px',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              left: 0,
              display: 'block',
              height: '10px',
              overflow: 'hidden',
              borderRadius: hasExceededMaxHours ? '5px 0 0 5px' : '5px',
              backgroundColor: 'background.success',
            }}
          >
            <Box
              sx={{
                display: 'block',
                width: `${progressPercent}%`,
                height: '100%',
                borderRadius: 'inherit',
                backgroundColor: 'success.main',
              }}
            />
          </Box>

          {shouldShowMinAllocationMarker && (
            <Box
              sx={{
                position: 'absolute',
                top: '-4px',
                left: `${minAllocationMarkerPercent}%`,
                width: '1px',
                height: '18px',
                backgroundColor: 'success.main',
              }}
            />
          )}

          {hasExceededMaxHours && (
            <Box
              sx={{
                position: 'absolute',
                top: '-4px',
                right: 0,
                width: '1px',
                height: '18px',
                backgroundColor: 'success.main',
              }}
            />
          )}

          <Typography
            variant="body2"
            component="span"
            lineHeight={1}
            sx={{
              position: 'absolute',
              top: '20px',
              left: 0,
            }}
          >
            0
          </Typography>

          {shouldShowMinAllocationMarker && (
            <Typography
              variant="body2"
              component="span"
              color="textPrimary"
              lineHeight={1}
              sx={{
                position: 'absolute',
                top: '20px',
                left: `${minAllocationMarkerPercent}%`,
                transform: 'translateX(-50%)',
              }}
            >
              {formatScaleHours(allocationSummary.plannedMinHours)}
            </Typography>
          )}

          <Typography
            variant="body2"
            component="span"
            lineHeight={1}
            sx={{
              position: 'absolute',
              top: '20px',
              right: '-7px',
            }}
          >
            {formatScaleHours(allocationSummary.plannedMaxHours)}
          </Typography>
        </Box>

        {hasExceededMaxHours && (
          <>
            <Box
              sx={{
                flex: `0 0 ${overflowWidthPercent}%`,
                height: '10px',
                boxSizing: 'border-box',
                border: 1,
                borderLeft: 0,
                borderStyle: 'dashed',
                borderColor: 'success.main',
                borderRadius: '0 5px 5px 0',
              }}
            />
            <Typography variant="body2" color="textSecondary" lineHeight={1} flex="0 0 auto" ml={0.75}>
              +{formatHours(allocationSummary.exceededMaxHours)}
            </Typography>
          </>
        )}
      </Box>
    </Tooltip>
  );
};

export default SyberryTodayAllocationScale;
